import { Controller } from "@hotwired/stimulus"

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      reset: (id?: string) => void
    }
  }
}

const TURNSTILE_API = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

type Pending = { widget: HTMLElement; next: number; prevState: number; prevScore: number }

// One page-level controller for ALL reaction widgets in its scope. On connect it
// makes a SINGLE batched request for this viewer's existing reactions (the
// hole-punch — the surrounding page is CDN-cached and shared, so per-viewer
// state can never live in its HTML), then hydrates each widget. Clicks toggle
// optimistically and POST/DELETE to the uncached write endpoint.
//
// CSRF: the page is cached, so its <meta> token is stale — we use the fresh
// token the states endpoint returns. Turnstile: an anonymous visitor's first
// write 403s with turnstile_required; we lazy-load the challenge (the widget
// markup is baked in, the script only loads on demand), verify-on-solve sets a
// 24h cookie, and the pending reaction is retried.
export default class extends Controller<HTMLElement> {
  static targets = ["turnstile"]
  static values = {
    statesUrl: { type: String, default: "/arena/reactions/states" },
    postUrl: { type: String, default: "/arena/reactions" },
    verifyUrl: { type: String, default: "/arena/reactions/verify" },
    origin: { type: String, default: "" },
  }

  declare statesUrlValue: string
  declare postUrlValue: string
  declare verifyUrlValue: string
  declare originValue: string
  declare readonly hasTurnstileTarget: boolean
  declare readonly turnstileTarget: HTMLElement

  private csrf = ""
  private busy = new Set<string>()
  private pending: Pending | null = null
  private turnstileRendered = false

  connect() {
    this.hydrate()
  }

  private widgets(): HTMLElement[] {
    return Array.from(this.element.querySelectorAll<HTMLElement>("[data-reactable]"))
  }

  private async hydrate() {
    const keys = this.widgets().map((w) => w.dataset.reactable!).filter(Boolean)
    if (keys.length === 0) return

    try {
      const url = `${this.statesUrlValue}?keys=${encodeURIComponent(keys.join(","))}`
      const res = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!res.ok) return
      const data = await res.json()
      this.csrf = data.csrf || ""
      this.widgets().forEach((w) => {
        const key = w.dataset.reactable!
        const mine = data.states?.[key]
        this.setState(w, typeof mine === "number" ? mine : 0)
        // The baked score is only an initial value; the live net score rides
        // this same uncached call, so refresh it (cached pages never go stale).
        const score = data.scores?.[key]
        if (typeof score === "number") this.setScore(w, score)
      })
    } catch {
      // network hiccup — buttons still work, they just start un-hydrated
    }
  }

  toggle(event: Event) {
    const button = event.currentTarget as HTMLElement
    const widget = button.closest<HTMLElement>("[data-reactable]")
    if (!widget) return

    // Ignore clicks while this widget already has a write in flight, so rapid
    // double-clicks can't race two POST/DELETEs into an inconsistent state.
    const key = widget.dataset.reactable!
    if (this.busy.has(key)) return

    const value = Number(button.dataset.value)
    const current = Number(widget.dataset.state || "0")
    const next = current === value ? 0 : value // clicking the active arrow clears it

    const prevScore = this.score(widget)
    this.setState(widget, next)
    this.setScore(widget, prevScore + (next - current)) // optimistic delta

    this.busy.add(key)
    this.send(widget, next, current, prevScore).finally(() => this.busy.delete(key))
  }

  private async send(widget: HTMLElement, next: number, prevState: number, prevScore: number) {
    try {
      let res = await this.post(widget, next)
      // Stale CSRF (a concurrent request superseded the session): refresh + retry.
      if (res.status === 422) {
        await this.refreshCsrf()
        res = await this.post(widget, next)
      }
      // Unverified anonymous visitor: summon the inline Turnstile challenge and
      // keep the optimistic state; the reaction is retried after verification.
      if (res.status === 403 && (await res.clone().json().catch(() => ({}))).turnstile_required) {
        if (this.requestTurnstile({ widget, next, prevState, prevScore })) return
      }
      if (!res.ok) {
        this.revert(widget, prevState, prevScore)
        return
      }
      const data = await res.json()
      this.setState(widget, typeof data.mine === "number" ? data.mine : 0)
      this.setScore(widget, Number(data.score))
    } catch {
      this.revert(widget, prevState, prevScore)
    }
  }

  private post(widget: HTMLElement, next: number): Promise<Response> {
    const [type, id] = widget.dataset.reactable!.split(":")
    return fetch(this.postUrlValue, {
      method: next === 0 ? "DELETE" : "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrf },
      credentials: "same-origin",
      body: JSON.stringify({ reactable_type: type, reactable_id: id, value: next, origin: this.originValue }),
    })
  }

  private async refreshCsrf() {
    try {
      const res = await fetch(`${this.statesUrlValue}?keys=`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (res.ok) this.csrf = (await res.json()).csrf || this.csrf
    } catch {
      // keep the existing token
    }
  }

  // ── Inline Turnstile challenge ─────────────────────────────────────────────

  // Returns true if a challenge was shown (caller leaves the optimistic state in
  // place); false if no widget is available (caller reverts).
  private requestTurnstile(pending: Pending): boolean {
    if (!this.hasTurnstileTarget) return false
    this.pending = pending
    this.turnstileTarget.hidden = false
    this.renderTurnstile()
    return true
  }

  private async renderTurnstile() {
    const container = this.turnstileTarget.querySelector<HTMLElement>(".cf-turnstile")
    if (!container || this.turnstileRendered) return

    await this.ensureTurnstileScript()
    if (!window.turnstile || !container.dataset.sitekey) return

    window.turnstile.render(container, {
      sitekey: container.dataset.sitekey,
      theme: "auto",
      callback: (token: string) => this.onTurnstileSolved(token),
    })
    this.turnstileRendered = true
  }

  private ensureTurnstileScript(): Promise<void> {
    if (window.turnstile) return Promise.resolve()
    return new Promise((resolve) => {
      const existing = document.querySelector<HTMLScriptElement>("script[data-turnstile-api]")
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true })
        return
      }
      const s = document.createElement("script")
      s.src = TURNSTILE_API
      s.async = true
      s.defer = true
      s.dataset.turnstileApi = "1"
      s.addEventListener("load", () => resolve(), { once: true })
      s.addEventListener("error", () => resolve(), { once: true })
      document.head.appendChild(s)
    })
  }

  private async onTurnstileSolved(token: string) {
    try {
      const res = await fetch(this.verifyUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrf },
        credentials: "same-origin",
        body: JSON.stringify({ "cf-turnstile-response": token }),
      })
      if (!res.ok) {
        window.turnstile?.reset()
        return
      }
    } catch {
      window.turnstile?.reset()
      return
    }

    this.turnstileTarget.hidden = true
    const pending = this.pending
    this.pending = null
    if (pending) await this.send(pending.widget, pending.next, pending.prevState, pending.prevScore)
  }

  // Dismiss the challenge without verifying — revert the optimistic reaction.
  cancelTurnstile() {
    this.turnstileTarget.hidden = true
    if (this.pending) {
      this.revert(this.pending.widget, this.pending.prevState, this.pending.prevScore)
      this.pending = null
    }
  }

  private revert(widget: HTMLElement, state: number, score: number) {
    this.setState(widget, state)
    this.setScore(widget, score)
  }

  // The active up/down highlight rides on data-state via Tailwind
  // group-data-[state=...] variants in the partial; transition-colors there gives
  // the hydration fade. No imperative class toggles needed.
  private setState(widget: HTMLElement, value: number) {
    widget.dataset.state = String(value)
  }

  private score(widget: HTMLElement): number {
    return Number(widget.querySelector<HTMLElement>("[data-score]")?.textContent || "0")
  }

  private setScore(widget: HTMLElement, value: number) {
    const el = widget.querySelector<HTMLElement>("[data-score]")
    if (el) el.textContent = String(value)
  }
}
